(ns admin.components.forms.resource-metadata
  (:require
   [reagent-form.core :as rf]
   [reagent-form.validators :as v]
   [reagent-form.transformers :as t]
   [re-frame.core :as re-frame]

   [admin.events.resources :as >resources]
   [admin.events.activity-resources :as >activity-resources]

   [kyber.components.base.button :as button]
   [kyber.components.base.text :as text]
   [kyber.components.form-fields.select :as select]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.lib.kyber-db :as kyber-db]))

(def classes
  (styles->classes
   {:cta-container
    {:display :flex
     :justify-content :flex
     :align-items :flex-end}

    :uploader-form
    {:width "100%"
     :padding (gs [:spacing :p20])}

    :info-message
    {:margin-top (gs [:spacing :p24])
     :padding [(gs [:spacing :p8])
               (gs [:spacing :p20])]
     :background-color (gs [:colors :info-100])
     :border (str "1px solid " (gs [:colors :info-200]))
     :border-radius (gs [:radius :r7])}}))

(defn render
  ([options]
   [render options nil])
  ([{:keys [on-cancel
            on-upload-start
            on-resource-removed
            on-success
            upload-state
            activity-uuid
            curriculum-uuid
            learning-objectives
            is-plo-upload?
            show-custom-version-id?]
     :or {on-cancel identity
          on-upload-start identity
          learning-objectives []
          show-custom-version-id? false}}
    {resource-uuid :resource/uuid
     file-name :resource/name
     description :resource/description
     learning-objective :resource/learning-objective
     :as file}]
   (let [is-new (nil? file)
         original-file (some-> upload-state deref :original-file)
         is-dispatching (re-frame/subscribe [:request/any-dispatching
                                             ::>resources/get-plo-file-upload-key
                                             ::>activity-resources/update-activity-resource])]
     [rf/form {:id (if (true? is-new)
                     :resources/upload-resource
                     :resources/update-resource)
               :is-submitting is-dispatching}
      [:form
       {:class (:uploader-form classes)
        :rf/form
        {:on-submit
         (fn [{:resource/keys [learning-objective] :as payload}]
           (let [learning-objective-uuid (:learning-objective/uuid learning-objective)
                 update-resource
                 (fn [resource-uuid success-message]
                   (re-frame/dispatch
                    [::>activity-resources/update-activity-resource
                     (assoc payload
                            :resource/uuid resource-uuid
                            :resource/activity {:activity/uuid activity-uuid}
                            :resource/curriculum {:curriculum/uuid curriculum-uuid})
                     (cond-> {:response-keys (into (kyber-db/get-model-keys :resource)
                                                   [:activity/uuid
                                                    {:resource/learning-objective
                                                     [:learning-objective/uuid
                                                      :learning-objective/label
                                                      :learning-objective/description]}])

                              :on-success
                              #(do (when (fn? on-success)
                                     (on-success %))
                                   (when is-new
                                     (swap! upload-state assoc :is-uploading false)))
                              :on-failure-toast "Unable to update resource. Please re-upload resource and try again."}

                       (some? success-message)
                       (assoc :on-success-toast success-message))]))]

             (when (and (seq learning-objective-uuid)
                        (true? is-new))
               (swap! upload-state assoc :learning-objective learning-objective-uuid))

             (cond
               (true? is-new)
               (do
                 (on-upload-start payload)
                 (re-frame/dispatch
                  [::>activity-resources/upload-activity-resource
                   (cond-> {:upload-state upload-state
                            :activity-uuid activity-uuid
                            :is-plo-upload? is-plo-upload?
                            :resource-metadata payload
                            :on-resource-created #(update-resource
                                                   (:resource/uuid %)
                                                   (when-not is-plo-upload?
                                                     "Resource Added"))}
                     (true? is-plo-upload?)
                     (assoc :on-resource-uploaded on-success))]))

               (false? is-new)
               (update-resource
                resource-uuid
                "Resource Updated"))))}}

       (rf/input
        (cond-> {:field-key :resource/name
                 :id :name
                 :type :text
                 :label "Name"
                 :validators [{:validator v/required
                               :message "Required"}]
                 :transformers [t/trim]}
          (true? is-new)
          (assoc :default-value (.-name original-file))

          (some? file-name)
          (assoc :default-value file-name)))

       ;; Custom ID field only for compliant PLO uploads
       (when show-custom-version-id?
         (rf/input
          {:field-key :resource/custom-version-id
           :id :custom-version-id
           :type :text
           :label "Custom ID"
           :validators [{:validator v/required
                         :message "Required"}]
           :transformers [t/trim]}))

       (rf/custom-field
        {:render select/render
         :label "Learning Objective"
         :field-key :resource/learning-objective
         :validators []
         :transformers [(fn [value]
                          (if (= value :none)
                            nil
                            {:learning-objective/uuid value}))]
         :default-value (or (:learning-objective/uuid learning-objective)
                            :none)
         :options (into [{:display (if (seq learning-objectives) "Select a learning objective"
                                       "No learning objectives")
                          :value :none}]
                        (->> learning-objectives
                             (sort-by :learning-objective/label)
                             (sort-by :learning-objective/ordinal)
                             (mapv (fn [{:learning-objective/keys [uuid label]}]
                                     {:display label :value uuid}))))})

       (when (empty? learning-objectives)
         [text/caption-30 :p
          "Create learning objectives below to assign one."])

       (rf/textarea
        (cond-> {:field-key :resource/description
                 :id :description
                 :label "Resource Description"
                 :validators []
                 :transformers [t/trim]}
          (some? description)
          (assoc :default-value description)))

       (when (false? is-new)
         [:div {:class (:info-message classes)}
          [text/body-20 :p
           [:span
            [:span {:style {:font-weight (gs [:font-weight :title-10])}}
             "Important: "]
            (str " Changes to the title and description will apply to all "
                 "Activities that this Resource is used.")]]])

       [:div {:class (:cta-container classes)
              :style {:justify-content :space-between}}
        [:div {:class (:cta-container classes)}
         (rf/submit-button
          {:default-text (if (true? is-new)
                           "Save & Upload"
                           "Update Resource")})

         [button/primary-button
          {:style {:margin-left (gs [:spacing :p20])}
           :on-click #(do
                        (.preventDefault %)
                        (on-cancel))}
          (if (true? is-new)
            "Cancel Upload"
            "Back")]]

        (when (and (false? is-new)
                   (some? on-resource-removed))
          [button/admin-button
           {:on-click #(do
                         (.preventDefault %)
                         (on-resource-removed file))}
           "Remove Resource"])]]])))
